/**
 * Редактор «Повернення від покупця» (рішення власника 2026-07-28):
 * повноцінна сторінка замість Drawer — «реалізація навпаки».
 *
 * - Створення «на підставі»: попап ПРОВЕДЕНИХ реалізацій покупця →
 *   рядки заповнюються ЗАЛИШКОМ до повернення (продано − вже повернуто).
 * - Частковий возврат: кількість рядка редагується (max = залишок),
 *   рядок можна прибрати; суми перераховуються пропорційно ціні.
 * - Проведення: товар назад на склад + реверс проводок (Дт 281—Кт 902,
 *   Дт 702—Кт 361, Дт 6432—Кт 702; на 6412 — проведенням РК, ч.1445) — існуючий постинг.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsReturnsFromCustomer, type GRFCItem,
} from '@/api/goodsReturns';
import { goodsSales } from '@/api/goodsSales';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import { api } from '@/api/client';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface ReturnableLine {
  nomenclature_id: string;
  characteristic_id: string | null;
  series_id: string | null;
  quantity_sold: string;
  quantity_returned: string;
  quantity_left: string;
  price: string;
  vat_rate_id: string;
}

interface Row extends Omit<GRFCItem, 'id' | 'cost_amount'> {
  /** Залишок до повернення (max для кількості; UI-підказка). */
  max_qty?: number;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  base_sale_id: string;
  warehouse_id: string;
  comment: string;
  // 0401: накладна ПОКУПЦЯ на повернення (вхідний первинний документ).
  buyer_doc_number: string;
  buyer_doc_date: string;
  items: Row[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  base_sale_id: '',
  warehouse_id: '',
  comment: '',
  buyer_doc_number: '',
  buyer_doc_date: '',
  items: [],
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ч.861: іконка документа для кнопок «заповнити з …» (як у рахунку/реалізації). */
function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function GoodsReturnFromCustomerEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const {
    availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('sales.return.manage');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [salePickerOpen, setSalePickerOpen] = useState(false);
  // РК до вихідної ПН (перенесено зі старого Viewer, 2026-08-06):
  // доступно і БЕЗ проводки повернення (рішення власника).
  const [rkOpen, setRkOpen] = useState(false);
  const [rkInvoiceId, setRkInvoiceId] = useState('');
  const [rkNumber, setRkNumber] = useState('');
  const [rkDate, setRkDate] = useState(new Date().toISOString().slice(0, 10));
  const [rkReason, setRkReason] = useState('103');
  // 2026-07-31 (рішення власника): вкладки як у реалізації.
  const [viewTab, setViewTab] = useState<'lines' | 'accounts' | 'tax'>('lines');
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  const numberPreview = useNumberPreview(
    'doc.goods_returns_from_customer', form.organization_id, isNew);

  const docQuery = useQuery({
    queryKey: ['grfc', id],
    queryFn: () => goodsReturnsFromCustomer.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'grfc', (cid) => goodsReturnsFromCustomer.get(cid));

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  // Проведені реалізації покупця — підстава повернення.
  const salesQ = useQuery({
    queryKey: ['sales-for-return', form.organization_id, form.contractor_id],
    queryFn: () => goodsSales.list({
      organization_id: form.organization_id || undefined,
      contractor_id: form.contractor_id || undefined,
      posted: true, limit: 200,
    }),
    enabled: !!form.contractor_id,
  });

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        base_sale_id: d.base_sale_id ?? '',
        warehouse_id: d.warehouse_id ?? '',
        comment: d.comment ?? '',
        buyer_doc_number: d.buyer_doc_number ?? '',
        buyer_doc_date: d.buyer_doc_date ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
          account_code: it.account_code ?? null,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const vatPercentById = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of vatList.data ?? []) m.set(v.id, Number(v.rate_percent));
    return m;
  }, [vatList.data]);

  // Заповнити рядки ЗАЛИШКОМ до повернення обраної реалізації.
  async function applyFromSale(saleId: string) {
    try {
      const lines = await api
        .get<ReturnableLine[]>(`/goods-returns-from-customer/returnable/${saleId}`)
        .then((r) => r.data);
      const sale = await goodsSales.get(saleId);
      const rows: Row[] = lines
        .filter((l) => Number(l.quantity_left) > 0)
        .map((l, idx) => {
          const qty = Number(l.quantity_left);
          const price = Number(l.price);
          const awv = qty * price;
          const rate = vatPercentById.get(l.vat_rate_id) ?? 0;
          const vat = awv * rate / 100;
          return {
            line_number: idx + 1,
            nomenclature_id: l.nomenclature_id,
            characteristic_id: l.characteristic_id,
            series_id: l.series_id,
            quantity: String(qty),
            price: String(price),
            amount_without_vat: awv.toFixed(2),
            vat_rate_id: l.vat_rate_id,
            vat_amount: vat.toFixed(2),
            total_amount: (awv + vat).toFixed(2),
            max_qty: qty,
          };
        });
      if (rows.length === 0) {
        setError('За цією реалізацією все вже повернуто.');
        setSalePickerOpen(false);
        return;
      }
      const saleWh = sale.warehouse_id
        ?? sale.items.find((i) => (i as { warehouse_id?: string | null }).warehouse_id)
          ?.warehouse_id ?? '';
      setForm((f) => ({
        ...f,
        organization_id: sale.organization_id,
        contractor_id: sale.contractor_id,
        base_sale_id: saleId,
        warehouse_id: f.warehouse_id || saleWh || '',
        items: rows,
      }));
      setError(null);
      setSalePickerOpen(false);
    } catch (e) {
      setError(extractError(e));
    }
  }

  function setQty(i: number, qty: string) {
    setForm((f) => ({
      ...f,
      items: f.items.map((row, idx) => {
        if (idx !== i) return row;
        const q = Number(qty) || 0;
        const price = Number(row.price);
        const awv = q * price;
        const rate = vatPercentById.get(row.vat_rate_id) ?? 0;
        const vat = awv * rate / 100;
        return {
          ...row, quantity: qty,
          amount_without_vat: awv.toFixed(2),
          vat_amount: vat.toFixed(2),
          total_amount: (awv + vat).toFixed(2),
        };
      }),
    }));
  }
  function setItem(i: number, patch: Partial<Row>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((row, idx) => idx === i ? { ...row, ...patch } : row),
    }));
  }
  // Зміна ставки ПДВ (вкладка «Податковий облік») → пересчёт ПДВ/разом рядка.
  function setRate(i: number, rid: string) {
    setForm((f) => ({
      ...f,
      items: f.items.map((row, idx) => {
        if (idx !== i) return row;
        const awv = Number(row.amount_without_vat) || 0;
        const rate = vatPercentById.get(rid) ?? 0;
        const vat = awv * rate / 100;
        return {
          ...row, vat_rate_id: rid,
          vat_amount: vat.toFixed(2),
          total_amount: (awv + vat).toFixed(2),
        };
      }),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((row, idx) => ({ ...row, line_number: idx + 1 })),
    }));
  }

  function payloadItems() {
    return form.items
      .filter((it) => Number(it.quantity) > 0)
      .map((it, idx) => ({
        line_number: idx + 1,
        nomenclature_id: it.nomenclature_id,
        characteristic_id: it.characteristic_id,
        series_id: it.series_id,
        quantity: it.quantity,
        price: it.price,
        amount_without_vat: it.amount_without_vat,
        vat_rate_id: it.vat_rate_id,
        vat_amount: it.vat_amount,
        total_amount: it.total_amount,
        account_code: it.account_code || null,
      }));
  }
  function validate(): boolean {
    if (!form.base_sale_id) {
      setError('Оберіть реалізацію-підставу («заповнити з реалізації»).');
      return false;
    }
    if (payloadItems().length === 0) {
      setError('Немає рядків з кількістю > 0.');
      return false;
    }
    if (!form.warehouse_id) {
      setError('Оберіть склад повернення (куди приймається товар).');
      return false;
    }
    const over = form.items.find((it) =>
      it.max_qty != null && Number(it.quantity) > it.max_qty);
    if (over) {
      const nm = nomList.data?.items.find((n) => n.id === over.nomenclature_id)?.name;
      setError(`«${nm ?? 'Рядок'}»: кількість більша за залишок до повернення (${over.max_qty}).`);
      return false;
    }
    return true;
  }

  const create = useMutation({
    mutationFn: () => goodsReturnsFromCustomer.createFromSale({
      organization_id: form.organization_id,
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      base_sale_id: form.base_sale_id,
      warehouse_id: form.warehouse_id || null,
      comment: form.comment || null,
      buyer_doc_number: form.buyer_doc_number || null,
      buyer_doc_date: form.buyer_doc_date || null,
      items: payloadItems(),
    } as never),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
      navigate(`/goods-returns-from-customer/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.patch(`/goods-returns-from-customer/${id}`, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_id: form.warehouse_id || null,
      comment: form.comment || null,
      buyer_doc_number: form.buyer_doc_number || null,
      buyer_doc_date: form.buyer_doc_date || null,
      items: payloadItems(),
    }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grfc', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsReturnsFromCustomer.createFromSale({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        base_sale_id: form.base_sale_id,
        warehouse_id: form.warehouse_id || null,
        comment: form.comment || null,
        buyer_doc_number: form.buyer_doc_number || null,
        buyer_doc_date: form.buyer_doc_date || null,
        items: payloadItems(),
      } as never);
      try {
        await goodsReturnsFromCustomer.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/goods-returns-from-customer/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
      navigate(`/goods-returns-from-customer/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await api.patch(`/goods-returns-from-customer/${id}`, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id || null,
        comment: form.comment || null,
        buyer_doc_number: form.buyer_doc_number || null,
        buyer_doc_date: form.buyer_doc_date || null,
        items: payloadItems(),
      });
      await goodsReturnsFromCustomer.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grfc', id] });
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsReturnsFromCustomer.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grfc', id] });
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsReturnsFromCustomer.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
      navigate('/goods-returns-from-customer');
    },
  });

  // РК до вихідної ПН: коригується лише ПРОВЕДЕНА ПН (вимога backend).
  const invoicesQ = useQuery({
    queryKey: ['outgoing-invoices-for-rk', form.contractor_id],
    queryFn: () => taxInvoicesOutgoing.list({
      contractor_id: form.contractor_id, posted: true, limit: 200,
    }),
    enabled: rkOpen && !!form.contractor_id,
  });
  const createRk = useMutation({
    mutationFn: () => taxInvoicesOutgoing.createCorrectionFromReturn({
      organization_id: form.organization_id,
      return_id: id!,
      corrected_invoice_id: rkInvoiceId,
      invoice_number: rkNumber,
      invoice_date: rkDate,
      correction_reason: rkReason || null,
    }),
    onSuccess: (rk) => {
      setRkOpen(false);
      navigate(`/tax-invoices-outgoing/${rk.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <p style={{ padding: '1rem' }}>
          У вас немає права створювати повернення у жодній з {totalAccessibleOrgs} організацій.
        </p>
      </div>
    );
  }

  const totalNoVat = form.items.reduce((s, it) => s + Number(it.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, it) => s + Number(it.vat_amount || 0), 0);
  const total = totalNoVat + totalVat;

  // ч.861: «ще дії» → права колонка «Повʼязані дії» (єдина дія збережена).
  const railActions = [{
    label: 'створити РК до вихідної ПН',
    title: isNew ? 'Спершу збережіть документ'
      : 'Скласти розрахунок коригування до раніше виписаної ПН на суму цього повернення',
    disabled: isNew,
    onClick: () => setRkOpen(true),
  }];
  // ч.861: прогрес — ЛИШЕ обовʼязкові поля (підстава-реалізація обовʼязкова:
  // без неї документ не зберігається).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Покупець і підстава', required: 3,
      filled: (form.contractor_id ? 1 : 0) + (form.warehouse_id ? 1 : 0) + (form.base_sale_id ? 1 : 0) },
    { id: 'buyerdoc', title: 'Накладна покупця', required: 0, filled: 0 },
    { id: 'items', title: 'Товари', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.861 (новий дизайн, Закон #10). «ще дії» → права колонка. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове повернення від покупця' : `Повернення: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            isNew={isNew}
            isPosted={isPosted}
            onBackToList={() => navigate('/goods-returns-from-customer')}
            onSave={() => { if (validate()) (isNew ? create.mutate() : update.mutate()); }}
            // Повернення без реалізації-підстави не існує (base_sale_id обовʼязковий).
            saveDisabled={!form.base_sale_id}
            savePending={create.isPending || update.isPending}
            onPost={() => { if (validate()) (isNew ? createAndPost.mutate() : updateAndPost.mutate()); }}
            postPending={createAndPost.isPending || updateAndPost.isPending}
            postDisabled={!form.base_sale_id || form.items.length === 0}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити повернення?').then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            onPrint={() => navigate(`/goods-returns-from-customer/${id}/print`)}
            entriesDoc={{ type: 'doc.goods_returns_from_customer', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
          />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/goods-returns-from-customer/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.goods_returns_from_customer', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value, base_sale_id: '', items: [] })}
                  disabled={!isNew}>
            <option value="">— оберіть юрособу —</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted}
                 placeholder={isNew ? (numberPreview ?? 'auto') : undefined} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      {/* Рядок 2 (дзеркало реалізації): Покупець | заповнити з реалізації. */}
      </DocBlock>

      <DocBlock index={2} title="Покупець і підстава">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Покупець *</label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.contractor_id}
            onChange={(cid) => setForm({ ...form, contractor_id: cid, base_sale_id: '', items: [] })}
            disabled={isPosted || !isNew || !!form.base_sale_id}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Склад повернення *{' '}
            <small style={{ color: 'var(--muted)' }}>(куди приймається товар)</small>
          </label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— оберіть —</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          {/* ч.861: «Підстава» — назва поля, документ під нею; кнопка — лише поки
              звʼязку немає (як у рахунку/реалізації, ч.854/855). */}
          <label>{form.base_sale_id ? 'Підстава *' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!form.base_sale_id && (
            <button type="button" className="btn-doc"
              onClick={() => setSalePickerOpen(true)}
              disabled={isPosted || !isNew || !form.contractor_id}
              title={!form.contractor_id
                  ? 'Спершу оберіть покупця'
                  : 'Обрати проведену реалізацію: рядки заповняться залишком до повернення'}>
              <DocIcon />заповнити з реалізації
            </button>
            )}
            {form.base_sale_id && (() => {
              const basisSale = salesQ.data?.items.find((x) => x.id === form.base_sale_id);
              return (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                    onClick={() => navigate(`/goods-sales/${form.base_sale_id}`)}>
                    реалізація{basisSale
                      ? ` №${basisSale.document_number} від ${new Date(basisSale.document_date).toLocaleDateString('uk-UA')}`
                      : ''}
                  </a>
                  {isNew && !isPosted && (
                    <button type="button" className="btn-x"
                      onClick={() => setForm({ ...form, base_sale_id: '', items: [] })}
                      title="Зняти звʼязок з реалізацією і очистити рядки">×</button>
                  )}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      </DocBlock>

      <DocBlock index={3} title="Накладна покупця">
      {/* 0401: накладна ПОКУПЦЯ на повернення — вхідний первинний документ.
          Підстава ОБЛІКУ лишається наша реалізація (base_sale_id); тут
          фіксуємо реквізити накладної, яку виписав покупець. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Накладна покупця №{' '}
            <small style={{ color: 'var(--muted)' }}>(документ, з яким приїхав товар)</small>
          </label>
          <input value={form.buyer_doc_number}
                 onChange={(e) => setForm({ ...form, buyer_doc_number: e.target.value })}
                 disabled={isPosted}
                 placeholder="№ накладної на повернення від покупця" />
        </div>
        <div className="field">
          <label>Дата накладної покупця</label>
          <DatePopover value={form.buyer_doc_date}
                 onChange={(v) => setForm({ ...form, buyer_doc_date: v })}
                 disabled={isPosted} />
        </div>
        <div />
      </div>

      </DocBlock>

      <DocBlock index={4} title="Товари">
      {/* 2026-07-31 (рішення власника): вкладки як у реалізації. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={viewTab === 'lines' ? 'active' : ''}
            onClick={() => setViewTab('lines')}>Рядки ({form.items.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab('accounts')}>Рахунки обліку</button>
          <button type="button" className={viewTab === 'tax' ? 'active' : ''}
            onClick={() => setViewTab('tax')}>Податковий облік</button>
        </div>
      </div>
      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 200 }}
                    title="Рахунок запасів рядка — куди повертається товар (порожньо — авто: 281)">
                  Рах. обліку (запаси)</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const nomAcc = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
                return (
                  <tr key={i}>
                    <td>{nomAcc?.name ?? '—'}</td>
                    <td>
                      <input value={it.account_code ?? ''} maxLength={10}
                        onChange={(e) => setItem(i, { account_code: e.target.value || null })}
                        disabled={isPosted}
                        placeholder="авто: 281"
                        style={{ width: '100%' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожнє поле — рахунок підбирається автоматично (типовий 281).
            Впишіть свій код, щоб перебити.
          </p>
        </div>
      )}
      {viewTab === 'tax' && (
        <TchTable style={{ marginTop: '0.5rem' }}>
          <thead>
            <tr style={{ textTransform: 'none' }}>
              <th>Номенклатура</th>
              <th style={{ width: 180 }}>Ставка ПДВ</th>
              <th style={{ width: 120, textAlign: 'right' }}>Сума ПДВ</th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => {
              const nomTax = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
              return (
                <tr key={i}>
                  <td>{nomTax?.name ?? '—'}</td>
                  <td>
                    <select value={it.vat_rate_id} disabled={isPosted}
                      onChange={(e) => setRate(i, e.target.value)}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {fmt(Number(it.vat_amount))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
      {viewTab === 'lines' && (
      <TchTable style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Номенклатура</th>
            <th style={{ width: 130 }}
                title="Кількість до повернення (максимум — залишок за реалізацією)">Кількість</th>
            <th style={{ width: 110 }}>Ціна</th>
            <th style={{ width: 120 }}>Сума без ПДВ</th>
            <th style={{ width: 110 }}>Сума ПДВ</th>
            <th style={{ width: 120 }}>Усього</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.length === 0 && (
            <tr><td colSpan={8} style={{ color: 'var(--muted)' }}>
              Оберіть покупця і натисніть «заповнити з реалізації».
            </td></tr>
          )}
          {form.items.map((it, i) => {
            const nom = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td>{nom?.name ?? '—'}</td>
                <td>
                  <input type="number" step="0.001" min={0} max={it.max_qty}
                         value={it.quantity}
                         onChange={(e) => setQty(i, e.target.value)}
                         disabled={isPosted} style={{ width: '100%' }} />
                  {it.max_qty != null && (
                    <small style={{ color: 'var(--muted)' }}>до {it.max_qty}</small>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(Number(it.price))}</td>
                <td style={{ textAlign: 'right' }}>{fmt(Number(it.amount_without_vat))}</td>
                <td style={{ textAlign: 'right' }}>{fmt(Number(it.vat_amount))}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
                <td>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        {form.items.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right' }}>Усього:</td>
              <td style={{ textAlign: 'right' }}>{fmt(totalNoVat)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(total)}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </TchTable>
      )}

      </DocBlock>

      <DocBlock index={5} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.goods_returns_from_customer'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {salePickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setSalePickerOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Проведені реалізації покупця</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setSalePickerOpen(false)}>закрити</button>
            </div>
            {(salesQ.data?.items.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>Немає проведених реалізацій цього покупця.</p>
            )}
            <table className="tch">
              <tbody>
                {salesQ.data?.items.map((sl) => (
                  <tr key={sl.id} onClick={() => void applyFromSale(sl.id)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td>{sl.document_number}</td>
                    <td>
                      {new Date(sl.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(sl.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rkOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setRkOpen(false)}>
          <div className="card" style={{ width: 520, margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>РК до вихідної ПН</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setRkOpen(false)}>закрити</button>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              РК складається на основі цього повернення і коригує одну з раніше
              виписаних податкових накладних (зменшує зобовʼязання).
              Сума повернення: {fmt(total)} грн.
              Зменшуючий РК після відправки реєструє ПОКУПЕЦЬ у своєму кабінеті.
            </p>
            <div className="field">
              <label>ПН, що коригується *</label>
              <select value={rkInvoiceId} onChange={(e) => setRkInvoiceId(e.target.value)}>
                <option value="">— оберіть —</option>
                {invoicesQ.data?.items.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    №{inv.invoice_number} від {new Date(inv.invoice_date).toLocaleDateString('uk-UA')} — {fmt(Number(inv.total_amount))}
                  </option>
                ))}
              </select>
            </div>
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>№ РК{' '}
                  <small style={{ color: 'var(--muted)' }}>(порожньо — авто, наступний за послідовністю)</small>
                </label>
                <input value={rkNumber} onChange={(e) => setRkNumber(e.target.value)}
                  placeholder="авто" />
              </div>
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Дата РК</label>
                <DatePopover value={rkDate} onChange={setRkDate} />
              </div>
            </div>
            <div className="field">
              <label>Причина коригування (код)</label>
              <input value={rkReason} onChange={(e) => setRkReason(e.target.value)}
                placeholder="103 — повернення товару" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button onClick={() => setRkOpen(false)}>скасувати</button>
              <button className="primary"
                disabled={!rkInvoiceId || createRk.isPending}
                onClick={() => createRk.mutate()}>
                створити РК
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    const d = r?.data?.detail;
    if (typeof d === 'string') return d;
    return 'Сервер не зміг обробити запит.';
  }
  return String(e);
}
